import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import {
  Users,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  TrendingUp,
  Calendar,
  Mail,
  BookOpen,
  DollarSign,
  Activity,
  Edit,
  Trash2,
  Save,
  AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Separator } from '../ui/separator';
import axios from 'axios';

const ModernUserManagement = ({ user }) => {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get search highlight from navigation state
  const highlightUserId = location.state?.highlightUser;
  const searchQuery = location.state?.searchQuery;
  const [sortBy, setSortBy] = useState('created_at');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [expandedUser, setExpandedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ email: '', isVerified: false });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  useEffect(() => {
    fetchUsers();
    
    // Set search term if coming from search
    if (searchQuery) {
      setSearchTerm(searchQuery);
    }
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please login again.');
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get('/api/admin/users', config);
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('User fetch error:', err);
      if (err.code === 'ECONNREFUSED') {
        setError('Cannot connect to server. Please ensure the backend server is running on port 5000.');
      } else if (err.response?.status === 401) {
        setError('Authentication failed. Please login again.');
      } else if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to fetch users. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateUserVerification = async (idNumber, isVerified) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.put(`/api/admin/users/${idNumber}/verify`,
        { isVerified },
        config
      );
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id_number === idNumber 
            ? { ...u, is_verified: isVerified }
            : u
        )
      );
    } catch (err) {
      console.error('Verification update error:', err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to update user verification' });
    } finally {
      setActionLoading(false);
    }
  };

  const startEditUser = (user) => {
    setEditingUser(user.id_number);
    setEditForm({
      email: user.email,
      isVerified: user.is_verified
    });
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditForm({ email: '', isVerified: false });
  };

  const saveUserEdit = async (idNumber) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.put(`/api/admin/users/${idNumber}`, 
        { 
          email: editForm.email,
          isVerified: editForm.isVerified
        }, 
        config
      );
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id_number === idNumber 
            ? { ...u, email: editForm.email, is_verified: editForm.isVerified }
            : u
        )
      );
      
      setEditingUser(null);
      setEditForm({ email: '', isVerified: false });
      setMessage({ type: 'success', text: 'User updated successfully!' });
    } catch (err) {
      console.error('User update error:', err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to update user' });
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDeleteUser = (idNumber) => {
    setShowDeleteConfirm(idNumber);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const deleteUser = async (idNumber) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.delete(`/api/admin/users/${idNumber}`, config);
      
      // Remove from local state
      setUsers(prevUsers => prevUsers.filter(u => u.id_number !== idNumber));
      
      setShowDeleteConfirm(null);
      setMessage({ type: 'success', text: 'User deleted successfully!' });
    } catch (err) {
      console.error('User deletion error:', err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to delete user' });
    } finally {
      setActionLoading(false);
    }
  };

  // Filter and sort users
  const filteredAndSortedUsers = users
    .filter(user => {
      // Search filter
      const matchesSearch = user.id_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && user.is_verified) ||
        (statusFilter === 'inactive' && !user.is_verified);

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'id_number':
          return a.id_number.localeCompare(b.id_number);
        case 'email':
          return a.email.localeCompare(b.email);
        case 'is_verified':
          return (b.is_verified ? 1 : 0) - (a.is_verified ? 1 : 0);
        case 'created_at':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

  // Pagination calculations
  const totalPages = Math.ceil(filteredAndSortedUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const paginatedUsers = filteredAndSortedUsers.slice(startIndex, startIndex + usersPerPage);

  // Reset to page 1 when search, sort, or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, statusFilter]);

  // Clear message after 3 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-slate-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
                User Management
              </h1>
              <p className="text-slate-600 text-sm sm:text-base lg:text-lg">
                Manage and monitor user accounts and verification status
              </p>
            </div>
            <Button 
              onClick={fetchUsers}
              className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </motion.div>


        {/* Search and Filters */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <Card className="border-0 bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                    <Input
                      placeholder="Search by ID number or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {/* Status Filter Buttons */}
                  <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                    <button
                      onClick={() => setStatusFilter('all')}
                      className={`px-3 py-2 text-sm font-medium transition-colors ${
                        statusFilter === 'all'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setStatusFilter('active')}
                      className={`px-3 py-2 text-sm font-medium transition-colors border-l border-slate-200 ${
                        statusFilter === 'active'
                          ? 'bg-green-600 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      Active
                    </button>
                    <button
                      onClick={() => setStatusFilter('inactive')}
                      className={`px-3 py-2 text-sm font-medium transition-colors border-l border-slate-200 ${
                        statusFilter === 'inactive'
                          ? 'bg-gray-600 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      Inactive
                    </button>
                  </div>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="created_at">Sort by Registration Date</option>
                    <option value="id_number">Sort by ID Number</option>
                    <option value="email">Sort by Email</option>
                    <option value="is_verified">Sort by Status</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Users Table */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                All Users ({filteredAndSortedUsers.length})
              </CardTitle>
              <CardDescription>
                Manage user accounts, verification status, and permissions
                {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600">{error}</p>
                </div>
              )}

              {message.text && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mb-4 p-4 rounded-lg border ${
                    message.type === 'success'
                      ? 'bg-green-50 border-green-200 text-green-700'
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`}
                >
                  <p>{message.text}</p>
                </motion.div>
              )}

              <div className="space-y-4">
                {paginatedUsers.map((user, index) => (
                  <motion.div
                    key={user.id_number}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`border border-slate-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 ${
                      highlightUserId === user.id_number ? 'bg-green-50 border-green-200' : ''
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center space-x-4">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback className="bg-blue-100 text-blue-600 font-semibold">
                            {user.id_number.slice(-2)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate">
                            {user.id_number}
                          </h3>
                          <p className="text-sm text-slate-600 truncate">{user.email}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {user.semester_progress && (
                              <Badge variant="outline" className="text-xs">
                                {user.semester_progress} books
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {/* Active/Inactive Toggle */}
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium ${user.is_verified ? 'text-green-600' : 'text-gray-400'}`}>
                            {user.is_verified ? 'Active' : 'Inactive'}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateUserVerification(user.id_number, !user.is_verified);
                            }}
                            disabled={actionLoading}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                              user.is_verified ? 'bg-green-500' : 'bg-gray-300'
                            } ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                user.is_verified ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setExpandedUser(expandedUser === user.id_number ? null : user.id_number)}
                        >
                          {expandedUser === user.id_number ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    <AnimatePresence>
                      {expandedUser === user.id_number && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mt-4 pt-4 border-t border-slate-200"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <h4 className="font-medium text-slate-900">Registration Info</h4>
                              <div className="text-sm text-slate-600">
                                <p>Created: {new Date(user.created_at).toLocaleDateString()}</p>
                                {user.last_login && (
                                  <p>Last Login: {new Date(user.last_login).toLocaleDateString()}</p>
                                )}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <h4 className="font-medium text-slate-900">Activity</h4>
                              <div className="text-sm text-slate-600">
                                <p>Books Borrowed: {user.books_currently_borrowed || 0}</p>
                                <p>Total Borrowed: {user.total_borrowed || 0}</p>
                                <p>Status: <span className={user.is_verified ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>{user.is_verified ? 'Active' : 'Inactive'}</span></p>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <h4 className="font-medium text-slate-900">Actions</h4>
                              <div className="flex gap-2 flex-wrap">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => startEditUser(user)}
                                  disabled={actionLoading}
                                >
                                  <Edit className="h-4 w-4 mr-1" />
                                  Edit User
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => confirmDeleteUser(user.id_number)}
                                  disabled={actionLoading}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Remove User
                                </Button>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
              
              {filteredAndSortedUsers.length === 0 && (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No users found</h3>
                  <p className="text-slate-600">
                    {searchTerm ? 'Try adjusting your search terms' : 'No users have been registered yet'}
                  </p>
                </div>
              )}

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600">
                    Showing {startIndex + 1} to {Math.min(startIndex + usersPerPage, filteredAndSortedUsers.length)} of {filteredAndSortedUsers.length} users
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            className="w-8 h-8 p-0"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Edit User Modal */}
      <AnimatePresence>
        {editingUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
            >
              <h3 className="text-lg font-semibold mb-4">Edit User</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder="user@my.smciligan.edu.ph"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isVerified"
                    checked={editForm.isVerified}
                    onChange={(e) => setEditForm({ ...editForm, isVerified: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="isVerified" className="text-sm font-medium text-slate-700">
                    Verified User
                  </label>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button
                  onClick={() => saveUserEdit(editingUser)}
                  disabled={actionLoading}
                  className="flex-1"
                >
                  <Save className="h-4 w-4 mr-1" />
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  onClick={cancelEdit}
                  disabled={actionLoading}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
            >
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-red-600">Confirm Deletion</h3>
              </div>
              <p className="text-slate-600 mb-6">
                Are you sure you want to delete this user? This action cannot be undone and will remove all user data including borrowing history and fines.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  onClick={() => deleteUser(showDeleteConfirm)}
                  disabled={actionLoading}
                  className="flex-1"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete User
                </Button>
                <Button
                  variant="outline"
                  onClick={cancelDelete}
                  disabled={actionLoading}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModernUserManagement;
